/**
 * image-reveal.js
 * Fades below-the-fold <img> elements in as they load and enter the viewport.
 * - While loading, the image carries `data-reveal-pending`, which theme.css holds
 *   at opacity 0.
 * - On load (or error) the attribute comes off and a Web Animations fade runs
 *   from 0 to the opacity the stylesheet gives that image. Nothing is written to
 *   the inline style, so CSS that hides an image on purpose (inactive PDP slides,
 *   the product-card hover image) and the image's own CSS transitions (blog-card
 *   hover scale) keep working afterwards. An inline `opacity: 1` left behind used
 *   to put the hidden slide and the hover image on top for good.
 * - LCP images (fetchpriority="high") fade faster (0.3s); everything else 0.55s.
 * - Images already loaded when they are armed are left alone (no flash).
 * - Eager heroes opt out with data-no-reveal; they fade via data-fade-in instead
 *   (see the head script in layout/theme.liquid).
 * - Does nothing under prefers-reduced-motion.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function duration(img) {
    return img.getAttribute('fetchpriority') === 'high' ? 300 : 550;
  }

  function hasOpacityTransition(cs) {
    var props = cs.transitionProperty.split(',');
    var durations = cs.transitionDuration.split(',');
    return props.some(function (prop, i) {
      prop = prop.trim();
      return (prop === 'opacity' || prop === 'all') && parseFloat(durations[i % durations.length]) > 0;
    });
  }

  function reveal(img) {
    img.removeAttribute('data-reveal-pending');
    var cs = getComputedStyle(img);
    // An image with its own opacity transition (product cards, PDP slides) fades
    // back by itself when the attribute comes off. Animating on top would read a
    // mid-transition opacity as the target and snap to the real one at the end.
    if (hasOpacityTransition(cs) || !img.animate) return;
    // With no opacity transition, the computed value is the one the stylesheet
    // wants: 1, a 0.15 texture, or 0 for an image that is hidden on purpose.
    var to = cs.opacity;
    if (to === '0') return;
    img.animate([{ opacity: 0 }, { opacity: to }], { duration: duration(img), easing: 'ease' });
  }

  function arm(img) {
    // Already loaded (or already failed): nothing to fade.
    if (img.complete) return;
    img.setAttribute('data-reveal-pending', '');
    img.addEventListener('load', function () { reveal(img); }, { once: true });
    img.addEventListener('error', function () { img.removeAttribute('data-reveal-pending'); }, { once: true });
  }

  // Intersection observer — arms images when they enter the viewport.
  // rootMargin adds a 120px buffer below the fold so images start fading
  // just before the user reaches them (anticipatory reveal).
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var img = entry.target;
      io.unobserve(img);
      arm(img);
    });
  }, { rootMargin: '0px 0px 120px 0px', threshold: 0 });

  function register(img) {
    // Skip decorative tiny images (icons, SVG logos ≤ 32px wide in the DOM)
    if (img.getAttribute('width') && parseInt(img.getAttribute('width'), 10) <= 32) return;
    // Skip explicitly opted-out images
    if (img.hasAttribute('data-no-reveal')) return;
    // Already cached — nothing to do
    if (img.complete && img.naturalWidth > 0) return;

    io.observe(img);
  }

  // Register all existing images on the page
  document.querySelectorAll('img').forEach(register);

  // Watch for images added later (AJAX cart drawer, section renders, etc.)
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'IMG') { register(node); return; }
        node.querySelectorAll('img').forEach(register);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

})();
